import React, { useEffect, useState, useCallback, useRef } from 'react';
import { MessageSquare, Send, Loader2, Info, StickyNote } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

/**
 * BookingChat — compact thread between patient & doctor for a single booking.
 *
 * Hard rule: MAX 3 messages per side (enforced server-side too).
 * Header shows a Note sign explaining the limit.
 */
export default function BookingChat({ bookingId, defaultOpen = false, compact = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [data, setData] = useState(null);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api.get(`/bookings/${bookingId}/messages`);
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Could not load messages');
    } finally { setLoading(false); }
  }, [bookingId]);

  useEffect(() => {
    if (!open || !bookingId) return;
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [open, bookingId, load]);

  useEffect(() => {
    if (endRef.current && data?.messages?.length) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [data?.messages?.length]);

  const send = async () => {
    const value = text.trim();
    if (!value) return;
    setSending(true); setError('');
    try {
      await api.post(`/bookings/${bookingId}/messages`, { text: value });
      setText('');
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Could not send');
    } finally { setSending(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-xs font-medium hover:bg-slate-200 transition-colors"
        data-testid={`booking-chat-open-${bookingId}`}>
        <MessageSquare size={12} /> Chat about this booking
      </button>
    );
  }

  const viewerRole = data?.viewer_role;
  const remaining = data?.remaining?.[viewerRole] ?? 0;
  const atLimit = data?.can_send === false;
  const limitPerSide = data?.limit_per_side ?? 3;
  const tier = data?.tier || 'free';
  const tierLabel = tier === 'turbo' ? 'Turbo · unlimited' : tier === 'premium' ? 'Coffee · 5/side' : 'Free · 3/side';

  return (
    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/50 overflow-hidden" data-testid={`booking-chat-panel-${bookingId}`}>
      {/* Header with Note */}
      <div className="px-4 py-3 bg-white border-b border-slate-200 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <MessageSquare size={14} className="text-sky-600" />
          <p className="text-sm font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Booking chat</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-semibold" title={`${limitPerSide} messages per side on ${tier} plan`} data-testid={`booking-chat-note-${bookingId}`}>
            <StickyNote size={11} />
            {tierLabel}
          </div>
          <button onClick={() => setOpen(false)} className="text-xs text-slate-500 hover:text-slate-700" data-testid={`booking-chat-close-${bookingId}`}>Hide</button>
        </div>
      </div>

      {/* Messages */}
      <div className="max-h-64 overflow-y-auto px-4 py-3 space-y-2.5" data-testid={`booking-chat-messages-${bookingId}`}>
        {loading && !data ? (
          <div className="flex items-center justify-center py-6"><Loader2 size={16} className="animate-spin text-slate-400" /></div>
        ) : data?.messages?.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-4">No messages yet. Start with a short note like "Running 10 mins late" or "Please bring past reports."</p>
        ) : (
          data?.messages?.map((m) => {
            const mine = m.sender_role === viewerRole;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-xs leading-relaxed ${mine ? 'bg-sky-600 text-white' : 'bg-white border border-slate-200 text-slate-800'}`} data-testid={`booking-msg-${m.id}`}>
                  <p className={`text-[10px] font-semibold mb-0.5 ${mine ? 'text-sky-100' : 'text-slate-500'}`}>{mine ? 'You' : m.sender_role === 'doctor' ? 'Doctor' : 'Patient'}</p>
                  <p className="whitespace-pre-wrap">{m.text}</p>
                  <p className={`text-[9px] mt-1 ${mine ? 'text-sky-200' : 'text-slate-400'}`}>{new Date(m.created_at).toLocaleString()}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      {/* Composer */}
      <div className="px-4 py-3 border-t border-slate-200 bg-white">
        {atLimit ? (
          <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-100 rounded-xl px-3 py-2" data-testid={`booking-chat-limit-${bookingId}`}>
            <Info size={12} /> You've used all {limitPerSide} messages for this booking. {tier === 'free' ? 'Ask the doctor to upgrade for higher limits.' : 'Please follow up by phone or WhatsApp.'}
          </div>
        ) : (
          <>
            <div className="flex items-end gap-2">
              <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={500} rows={compact ? 1 : 2}
                placeholder="Short note (max 500 chars)..."
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none text-xs resize-none"
                data-testid={`booking-chat-input-${bookingId}`} />
              <button onClick={send} disabled={sending || !text.trim()}
                className="flex items-center gap-1 px-3 py-2 rounded-xl bg-sky-600 text-white text-xs font-medium hover:bg-sky-700 transition-colors disabled:opacity-50"
                data-testid={`booking-chat-send-${bookingId}`}>
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send
              </button>
            </div>
            <p className="text-[10px] text-slate-400 mt-1.5">You have <b className="text-slate-600">{remaining}</b> of {limitPerSide} left on your side.</p>
          </>
        )}
        {error && <p className="text-[11px] text-red-600 mt-1.5">{error}</p>}
      </div>
    </div>
  );
}
